import React, { createElement, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components';
import useSWRImmutable from 'swr/immutable';
import useSWR from 'swr';
import { v4 as uuidV4 } from 'uuid';
import { TwoFactorAuthenticationMethods, TwoFactorAuthenticationStates, } from '@tradingblock/types';
import { GetAccountManagementDetailsUrl } from '@tradingblock/api';
import { Loading } from '../common';
import SuccessView from './views/Success';
import ContactInfoView from './views/ContactInfo';
import IntroView from './views/Intro';
import EnterSecurityCodeView from './views/EnterSecurityCode';
const StyledTwoFactorAuthenticationSetup = styled.div `
  .header-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .fa-cog {
    color: white;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .btn-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
  }
`;
var Views;
(function (Views) {
    Views["Intro"] = "Intro";
    Views["ContactInfo"] = "ContactInfo";
    Views["EnterSecurityCode"] = "EnterSecurityCode";
    Views["Success"] = "Success";
})(Views || (Views = {}));
;
export var ContextTypes;
(function (ContextTypes) {
    ContextTypes["login"] = "login";
    ContextTypes["accountManagementBlock"] = "accountManagementBlock";
})(ContextTypes || (ContextTypes = {}));
;
export var TwoFactorAuthenticationMethodVerificationStepsOverrideStates;
(function (TwoFactorAuthenticationMethodVerificationStepsOverrideStates) {
    TwoFactorAuthenticationMethodVerificationStepsOverrideStates["UserInitiatedSmsTwoFactorAuthenticationMethodVerification"] = "UserInitiatedSmsTwoFactorAuthenticationMethodVerification";
    TwoFactorAuthenticationMethodVerificationStepsOverrideStates["UserInitiatedEmailTwoFactorAuthenticationMethodVerification"] = "UserInitiatedEmailTwoFactorAuthenticationMethodVerification";
})(TwoFactorAuthenticationMethodVerificationStepsOverrideStates || (TwoFactorAuthenticationMethodVerificationStepsOverrideStates = {}));
;
;
;
export const TwoFactorAuthenticationSetup = ({ primaryBtnClassName, primaryBtnIcon, primaryBtnIconClassName, handleEmailUpdate, handlePhoneUpdate, mainHeaderHtmlTag, api, accountId, handleSetupComplete, mainHeaderIconClassName, mainHeaderIconStyles, mainHeaderStyles, rootRestHubUrl, handleAuthTokenExpiration, context, customRequestUserProfile, twoFactorAuthenticationMethodVerificationStepsOverrideState, allowAbilityToCancel, cancelBtnClassName, handleCancel, smsTwoFactorAuthenticationMethodVerifiedPhoneData, receiveAccountManagementDetails, siteGrp, }) => {
    const MainHeader = useCallback(() => createElement(mainHeaderHtmlTag, {
        style: mainHeaderStyles || {}
    }, 'Two-factor Authentication'), [mainHeaderHtmlTag, mainHeaderStyles]);
    const [errorMsg, setErrorMsg] = useState(null);
    const [primaryAccountHolderEmail, setPrimaryAccountHolderEmail] = useState(null);
    const [primaryAccountHolderPhones, setPrimaryAccountHolderPhones] = useState(null);
    const [twoFactorAuthenticationState, setTwoFactorAuthenticationState] = useState(null);
    const [verifyStepsData, setVerifyStepsData] = useState(null);
    const [verifyStepsIndex, setVerifyStepsIndex] = useState(0);
    const [phoneNumberToUseForTwoFactorAuthentication, setPhoneNumberToUseForTwoFactorAuthentication] = useState(null);
    const prevTwoFactorAuthenticationState = useRef(twoFactorAuthenticationState);
    // The message below should never get triggered but is here just in case this component in the dashboard gets a 200 response code prior to the web socket message.
    const fallbackAuthTokenExpiredMsg = 'You have been logged out. Please log in again.';
    const genericErrorMsg = `There was an issue on our end. ${context === 'login' ? 'Try refreshing the page.' : 'Try moving to a different tab within the account management block and try again.'} If the issue persists, please contact Customer Service.`;
    const uuidForGetProfileKey = useMemo(() => uuidV4(), []);
    const { isLoading: isLoadingUserProfileData, data: getUserProfileResponse, error: errorGettingUserProfileData, isValidating: isValidatingUserProfileData, mutate: getUserProfileData, } = useSWRImmutable(!twoFactorAuthenticationMethodVerificationStepsOverrideState ? `${rootRestHubUrl}/User/GetProfile${uuidForGetProfileKey}` : null, api.user.profile, { revalidateOnFocus: false, shouldRetryOnError: false });
    const { isLoading: isLoadingAccountManagementDetailsData, data: getAccountManagementDetailsResponse, error: errorGettingAccountManagementDetailsData, isValidating: isValidatingAccountManagementDetailsData, } = useSWR(accountId ? `${rootRestHubUrl}${GetAccountManagementDetailsUrl}?accountId=${accountId}` : null, () => (api.accounts.getUserAccountDetails({ accountId })), { revalidateOnFocus: false, shouldRetryOnError: false });
    useEffect(() => {
        return () => {
            if (customRequestUserProfile) {
                customRequestUserProfile();
            }
        };
    }, []);
    useEffect(() => {
        if (!twoFactorAuthenticationMethodVerificationStepsOverrideState) {
            if (getUserProfileResponse &&
                getUserProfileResponse.responseCode === 0 &&
                getUserProfileResponse.payload &&
                getUserProfileResponse.payload.twoFactorAuthenticationState) {
                setTwoFactorAuthenticationState(getUserProfileResponse.payload.twoFactorAuthenticationState);
            }
            else if (getUserProfileResponse &&
                getUserProfileResponse.responseCode === 200) {
                if (handleAuthTokenExpiration) {
                    handleAuthTokenExpiration();
                }
                else {
                    setErrorMsg(fallbackAuthTokenExpiredMsg);
                }
            }
            else if ((!isLoadingUserProfileData &&
                !isValidatingUserProfileData &&
                !getUserProfileResponse) ||
                (getUserProfileResponse &&
                    (!getUserProfileResponse.payload ||
                        !getUserProfileResponse.payload.twoFactorAuthenticationState ||
                        getUserProfileResponse.responseCode !== 0))) {
                setErrorMsg(genericErrorMsg);
            }
        }
    }, [
        getUserProfileResponse,
        isLoadingUserProfileData,
        isValidatingUserProfileData,
    ]);
    useEffect(() => {
        if (getAccountManagementDetailsResponse &&
            getAccountManagementDetailsResponse.responseCode === 0 &&
            getAccountManagementDetailsResponse.payload) {
            setPrimaryAccountHolderEmail(getAccountManagementDetailsResponse.payload.accountHolders[0].email);
            setPrimaryAccountHolderPhones(getAccountManagementDetailsResponse.payload.accountHolders[0].phones);
            if (receiveAccountManagementDetails) {
                receiveAccountManagementDetails(getAccountManagementDetailsResponse.payload);
            }
        }
        else if (getAccountManagementDetailsResponse &&
            getAccountManagementDetailsResponse.responseCode === 200) {
            if (handleAuthTokenExpiration) {
                handleAuthTokenExpiration();
            }
            else {
                setErrorMsg(fallbackAuthTokenExpiredMsg);
            }
        }
        else if ((!isLoadingAccountManagementDetailsData &&
            !isValidatingAccountManagementDetailsData &&
            !getAccountManagementDetailsResponse) ||
            (getAccountManagementDetailsResponse &&
                (!getAccountManagementDetailsResponse.payload ||
                    getAccountManagementDetailsResponse.responseCode !== 0))) {
            setErrorMsg(genericErrorMsg);
        }
    }, [
        getAccountManagementDetailsResponse,
        isLoadingAccountManagementDetailsData,
        isValidatingAccountManagementDetailsData,
    ]);
    useEffect(() => {
        if (errorGettingAccountManagementDetailsData ||
            errorGettingUserProfileData) {
            setErrorMsg(genericErrorMsg);
        }
    }, [
        errorGettingAccountManagementDetailsData,
        errorGettingUserProfileData,
    ]);
    // This will determine the set of steps needed for a custom verification.
    useEffect(() => {
        if (twoFactorAuthenticationMethodVerificationStepsOverrideState &&
            primaryAccountHolderEmail &&
            primaryAccountHolderPhones) {
            if (twoFactorAuthenticationMethodVerificationStepsOverrideState === TwoFactorAuthenticationMethodVerificationStepsOverrideStates.UserInitiatedEmailTwoFactorAuthenticationMethodVerification) {
                setVerifyStepsData([
                    {
                        view: Views.ContactInfo,
                        viewProps: {
                            contactInfoToDisplay: primaryAccountHolderEmail,
                            handleContactInfoUpdate: handleEmailUpdate,
                            primaryButtonProps: {
                                primaryBtnClassName,
                                primaryBtnIconClassName,
                                primaryBtnIcon,
                                onClick: () => setVerifyStepsIndex(verifyStepsIndex + 1),
                            },
                            twoFactorAuthenticationMethodToBeVerified: TwoFactorAuthenticationMethods.email,
                            context,
                            genericErrorMsg,
                            allowAbilityToCancel,
                            cancelBtnProps: {
                                className: cancelBtnClassName,
                                onClick: handleCancel
                            },
                        },
                    },
                    {
                        view: Views.EnterSecurityCode,
                        viewProps: {
                            twoFactorAuthenticationMethodToBeVerified: TwoFactorAuthenticationMethods.email,
                            primaryButtonProps: {
                                primaryBtnClassName,
                                primaryBtnIcon,
                                primaryBtnIconClassName,
                                btnType: 'submit'
                            },
                            contactInfoToUseForTwoFactorAuthentication: primaryAccountHolderEmail,
                            handleNextViewNavigation: () => setVerifyStepsIndex(verifyStepsIndex + 1),
                            fallbackAuthTokenExpiredMsg,
                            genericErrorMsg,
                            handleEmailUpdate,
                            rootRestHubUrl,
                            api,
                            handleAuthTokenExpiration,
                            context,
                            allowAbilityToCancel,
                            cancelBtnProps: {
                                className: cancelBtnClassName,
                                onClick: handleCancel,
                            },
                        },
                    },
                    {
                        view: Views.Success,
                        viewProps: {
                            verifiedTwoFactorAuthenticationMethod: TwoFactorAuthenticationMethods.email,
                            primaryButtonProps: {
                                primaryBtnClassName,
                                primaryBtnIcon,
                                primaryBtnIconClassName,
                                onClick: handleSetupComplete,
                                btnTextOverride: 'Close',
                            },
                        },
                    }
                ]);
            }
            else if (twoFactorAuthenticationMethodVerificationStepsOverrideState === TwoFactorAuthenticationMethodVerificationStepsOverrideStates.UserInitiatedSmsTwoFactorAuthenticationMethodVerification) {
                setVerifyStepsData([
                    {
                        view: Views.ContactInfo,
                        viewProps: {
                            contactInfoToDisplay: primaryAccountHolderPhones,
                            handleContactInfoUpdate: handlePhoneUpdate,
                            primaryButtonProps: {
                                primaryBtnClassName,
                                primaryBtnIcon,
                                primaryBtnIconClassName,
                                onClick: () => setVerifyStepsIndex(verifyStepsIndex + 1),
                            },
                            setPhoneNumberToUseForTwoFactorAuthentication,
                            twoFactorAuthenticationMethodToBeVerified: TwoFactorAuthenticationMethods.sms,
                            context,
                            phoneNumberToUseForTwoFactorAuthentication,
                            genericErrorMsg,
                            allowAbilityToCancel,
                            cancelBtnProps: {
                                className: cancelBtnClassName,
                                onClick: handleCancel,
                            },
                            smsTwoFactorAuthenticationMethodVerifiedPhoneData,
                        }
                    },
                    {
                        view: Views.EnterSecurityCode,
                        viewProps: {
                            twoFactorAuthenticationMethodToBeVerified: TwoFactorAuthenticationMethods.sms,
                            primaryButtonProps: {
                                primaryBtnClassName,
                                primaryBtnIcon,
                                primaryBtnIconClassName,
                                btnType: 'submit'
                            },
                            contactInfoToUseForTwoFactorAuthentication: phoneNumberToUseForTwoFactorAuthentication,
                            handleNextViewNavigation: () => setVerifyStepsIndex(verifyStepsIndex + 1),
                            fallbackAuthTokenExpiredMsg,
                            genericErrorMsg,
                            handleSelectDifferentPhoneNumber: () => setVerifyStepsIndex(verifyStepsIndex - 1),
                            rootRestHubUrl,
                            api,
                            handleAuthTokenExpiration,
                            context,
                            allowAbilityToCancel,
                            cancelBtnProps: {
                                className: cancelBtnClassName,
                                onClick: handleCancel,
                            },
                        },
                    },
                    {
                        view: Views.Success,
                        viewProps: {
                            verifiedTwoFactorAuthenticationMethod: TwoFactorAuthenticationMethods.sms,
                            primaryButtonProps: {
                                primaryBtnClassName,
                                primaryBtnIcon,
                                primaryBtnIconClassName,
                                onClick: handleSetupComplete,
                                btnTextOverride: 'Close',
                            },
                        }
                    }
                ]);
            }
        }
    }, [
        verifyStepsIndex,
        primaryAccountHolderPhones,
        primaryAccountHolderEmail,
        phoneNumberToUseForTwoFactorAuthentication,
    ]);
    // This will determine the set of steps needed for a standard verification based on the two-factor authentication state.
    useEffect(() => {
        if (twoFactorAuthenticationState &&
            !twoFactorAuthenticationMethodVerificationStepsOverrideState &&
            primaryAccountHolderEmail &&
            primaryAccountHolderPhones) {
            let tempVerifyStepsData = [];
            const twoFactorAuthenticationSetupCompleteSuccessViewData = {
                view: Views.Success,
                viewProps: {
                    twoFactorAuthenticationSetupComplete: true,
                    primaryButtonProps: {
                        primaryBtnClassName,
                        primaryBtnIcon,
                        primaryBtnIconClassName,
                        onClick: handleSetupComplete,
                    },
                },
            };
            if (twoFactorAuthenticationState === TwoFactorAuthenticationStates.EnabledWithSmsAndEmailMethodsVerified) {
                tempVerifyStepsData.push(twoFactorAuthenticationSetupCompleteSuccessViewData);
                setVerifyStepsData(tempVerifyStepsData);
                return;
            }
            if (((verifyStepsData &&
                ((verifyStepsData[0] &&
                    verifyStepsData[0].view === Views.Intro))) ||
                !verifyStepsData)) {
                tempVerifyStepsData.push({
                    view: Views.Intro,
                    viewProps: {
                        twoFactorAuthenticationState,
                        primaryButtonProps: {
                            primaryBtnClassName,
                            primaryBtnIcon,
                            primaryBtnIconClassName,
                            onClick: () => setVerifyStepsIndex(1),
                        },
                    },
                });
            }
            if (twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndNoVerifiedMethods ||
                twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndSmsMethodNotVerified ||
                twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndNoVerifiedMethods ||
                twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndSmsMethodNotVerified ||
                twoFactorAuthenticationState === TwoFactorAuthenticationStates.EnabledAndSmsMethodNotVerified) {
                tempVerifyStepsData.push({
                    view: Views.ContactInfo,
                    viewProps: {
                        contactInfoToDisplay: primaryAccountHolderPhones,
                        handleContactInfoUpdate: handlePhoneUpdate,
                        primaryButtonProps: {
                            primaryBtnClassName,
                            primaryBtnIcon,
                            primaryBtnIconClassName,
                            onClick: () => setVerifyStepsIndex(verifyStepsIndex + 1),
                        },
                        setPhoneNumberToUseForTwoFactorAuthentication,
                        twoFactorAuthenticationMethodToBeVerified: TwoFactorAuthenticationMethods.sms,
                        context,
                        phoneNumberToUseForTwoFactorAuthentication,
                        genericErrorMsg,
                    },
                });
                tempVerifyStepsData.push({
                    view: Views.EnterSecurityCode,
                    viewProps: {
                        twoFactorAuthenticationMethodToBeVerified: TwoFactorAuthenticationMethods.sms,
                        primaryButtonProps: {
                            primaryBtnClassName,
                            primaryBtnIcon,
                            primaryBtnIconClassName,
                            btnType: 'submit',
                        },
                        handleNextViewNavigation: () => setVerifyStepsIndex(verifyStepsIndex + 1),
                        fallbackAuthTokenExpiredMsg,
                        genericErrorMsg,
                        contactInfoToUseForTwoFactorAuthentication: phoneNumberToUseForTwoFactorAuthentication,
                        handleSelectDifferentPhoneNumber: () => setVerifyStepsIndex(verifyStepsIndex - 1),
                        api,
                        rootRestHubUrl,
                        handleAuthTokenExpiration,
                        context,
                    },
                });
                if (twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndSmsMethodNotVerified ||
                    twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndSmsMethodNotVerified) {
                    tempVerifyStepsData.push(twoFactorAuthenticationSetupCompleteSuccessViewData);
                }
                else {
                    tempVerifyStepsData.push({
                        view: Views.Success,
                        viewProps: {
                            verifiedTwoFactorAuthenticationMethod: TwoFactorAuthenticationMethods.sms,
                            primaryButtonProps: {
                                primaryBtnClassName,
                                primaryBtnIcon,
                                primaryBtnIconClassName,
                                onClick: () => {
                                    if (twoFactorAuthenticationState === TwoFactorAuthenticationStates.EnabledAndSmsMethodNotVerified) {
                                        handleSetupComplete();
                                    }
                                    else {
                                        getUserProfileData();
                                    }
                                },
                            },
                        },
                    });
                }
            }
            else if (twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndEmailMethodNotVerified ||
                twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndEmailMethodNotVerified ||
                twoFactorAuthenticationState === TwoFactorAuthenticationStates.EnabledAndEmailMethodNotVerified) {
                tempVerifyStepsData.push({
                    view: Views.ContactInfo,
                    viewProps: {
                        contactInfoToDisplay: primaryAccountHolderEmail,
                        handleContactInfoUpdate: handleEmailUpdate,
                        primaryButtonProps: {
                            primaryBtnClassName,
                            primaryBtnIcon,
                            primaryBtnIconClassName,
                            onClick: () => setVerifyStepsIndex(verifyStepsIndex + 1),
                        },
                        twoFactorAuthenticationMethodToBeVerified: TwoFactorAuthenticationMethods.email,
                        context,
                        genericErrorMsg,
                    },
                });
                tempVerifyStepsData.push({
                    view: Views.EnterSecurityCode,
                    viewProps: {
                        primaryButtonProps: {
                            primaryBtnClassName,
                            primaryBtnIcon,
                            primaryBtnIconClassName,
                            btnType: 'submit',
                        },
                        handleNextViewNavigation: () => setVerifyStepsIndex(verifyStepsIndex + 1),
                        fallbackAuthTokenExpiredMsg,
                        genericErrorMsg,
                        twoFactorAuthenticationMethodToBeVerified: TwoFactorAuthenticationMethods.email,
                        contactInfoToUseForTwoFactorAuthentication: primaryAccountHolderEmail,
                        handleEmailUpdate,
                        api,
                        rootRestHubUrl,
                        handleAuthTokenExpiration,
                        context,
                    }
                });
                if (twoFactorAuthenticationState === TwoFactorAuthenticationStates.DisabledByAdminAndEmailMethodNotVerified ||
                    twoFactorAuthenticationState === TwoFactorAuthenticationStates.FirstTimeSetupAndEmailMethodNotVerified) {
                    tempVerifyStepsData.push(twoFactorAuthenticationSetupCompleteSuccessViewData);
                }
                else {
                    tempVerifyStepsData.push({
                        view: Views.Success,
                        viewProps: {
                            verifiedTwoFactorAuthenticationMethod: TwoFactorAuthenticationMethods.email,
                            primaryButtonProps: {
                                primaryBtnClassName,
                                primaryBtnIcon,
                                primaryBtnIconClassName,
                                onClick: handleSetupComplete,
                            },
                        },
                    });
                }
            }
            if (tempVerifyStepsData.length > 0) {
                setVerifyStepsData(tempVerifyStepsData);
            }
        }
    }, [
        twoFactorAuthenticationState,
        verifyStepsIndex,
        phoneNumberToUseForTwoFactorAuthentication,
        primaryAccountHolderEmail,
        primaryAccountHolderPhones,
    ]);
    // This will reset the set of steps when the two-factor authentication state changes.
    useEffect(() => {
        if (twoFactorAuthenticationState &&
            prevTwoFactorAuthenticationState.current &&
            prevTwoFactorAuthenticationState.current !== twoFactorAuthenticationState) {
            setVerifyStepsIndex(0);
            setVerifyStepsData([]);
        }
    }, [twoFactorAuthenticationState]);
    useEffect(() => {
        prevTwoFactorAuthenticationState.current = twoFactorAuthenticationState;
    }, [twoFactorAuthenticationState]);
    const determineVerifyStepComponent = useCallback(() => {
        if (verifyStepsData && verifyStepsData[verifyStepsIndex]) {
            const currentVerifyStepData = verifyStepsData[verifyStepsIndex];
            switch (currentVerifyStepData.view) {
                case Views.Intro:
                    return (React.createElement(IntroView, Object.assign({}, currentVerifyStepData.viewProps)));
                case Views.ContactInfo:
                    return (React.createElement(ContactInfoView, Object.assign({}, currentVerifyStepData.viewProps)));
                case Views.EnterSecurityCode:
                    return (React.createElement(EnterSecurityCodeView, Object.assign({}, currentVerifyStepData.viewProps, { siteGrp: siteGrp })));
                case Views.Success:
                    return (React.createElement(SuccessView, Object.assign({}, currentVerifyStepData.viewProps)));
                default:
                    return null;
            }
        }
        else {
            return null;
        }
    }, [verifyStepsData, verifyStepsIndex]);
    return (React.createElement(StyledTwoFactorAuthenticationSetup, null,
        mainHeaderHtmlTag && (React.createElement("div", { className: "header-wrapper" },
            React.createElement("i", { className: `${mainHeaderIconClassName || ''} ${mainHeaderHtmlTag} fal fa-mobile-alt`, style: Object.assign({ marginRight: '10px' }, mainHeaderIconStyles) }),
            React.createElement(MainHeader, null))),
        (isLoadingAccountManagementDetailsData ||
            isLoadingUserProfileData ||
            isValidatingAccountManagementDetailsData ||
            isValidatingUserProfileData) && (React.createElement("div", { className: "loading-container" },
            React.createElement(Loading, null))),
        !errorMsg &&
            !isLoadingAccountManagementDetailsData &&
            !isValidatingAccountManagementDetailsData &&
            !isLoadingUserProfileData &&
            !isValidatingUserProfileData &&
            determineVerifyStepComponent(),
        (errorMsg &&
            !isLoadingAccountManagementDetailsData &&
            !isLoadingUserProfileData &&
            !isValidatingAccountManagementDetailsData &&
            !isValidatingUserProfileData)
            && React.createElement("p", { className: "error" }, errorMsg)));
};
//# sourceMappingURL=TwoFactorAuthenticationSetup.js.map